import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { isNumeric, Issue } from '../../constants';
import config from '../../config';

import { useCache } from '../../App.hooks';
import { useIssueNotFound } from '../../hooks/use-issue';

/**
 * React hook that returns the details of the issue
 *
 * @param args - Props to get issues
 *
 */
export function useGetIssue(id: string): GetIssueResponse {
	const [issue, setIssue] = useState<Issue | null>();
	const fetchPolicy = useCache(
		`preview:${id}`,
		config?.app?.cache?.ttl?.getIssue,
	);
	const { loading, error, data } = useQuery<{ issue: Issue | null }>(
		GET_ISSUE,
		{
			fetchPolicy,
			variables: { id, isSlug: !isNumeric(id) },
		},
	);

	useIssueNotFound(issue);

	useEffect(() => {
		if (loading) return;
		if (error) {
			console.error(error);
			return;
		}

		if (!data) return;
		setIssue(data.issue);
	}, [loading, error, data]);

	return {
		isLoading: loading,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

interface GetIssueResponse {
	issue: any | null | undefined;
	isLoading: boolean;
	error?: Error | null;
}

export const GET_ISSUE = gql`
	query PreviewIssue($id: ID!, $isSlug: Boolean!) {
		issue(id: $id, isSlug: $isSlug) {
			id
			name
			cover
			externalUrl
			slug
			description
			features
			data
			updated
			isPaid
			created
			releaseDate
			articles {
				id
				ArticleID
				role
				name
				channel
				features
				index
				slug
				description
				isFeatured
				styleProperties
				updated
				created
			}
		}
	}
`;
